import axios from "axios";
import type { AxiosResponse } from "axios";
import { apiBaseUrl } from "./config";
import { getStoredETag, storeETag, getCachedData, storeCachedData, buildCacheKey } from "./cache/etag";

export const apiClient = axios.create({
    baseURL: apiBaseUrl,
    withCredentials: true
});

// Note: isLoggingIn was previously used but is no longer needed

// Refresh token mutex to prevent multiple simultaneous refresh attempts
let refreshPromise: Promise<void> | null = null;
let isRefreshing = false;
let refreshFailed = false; // Track if refresh has already failed to prevent loops
let refreshAttempts = 0; // Track number of refresh attempts to prevent infinite loops
const MAX_REFRESH_ATTEMPTS = 1; // Only try refresh ONCE, then give up
let lastRefreshAttemptTime = 0; // Track last refresh attempt to prevent rapid retries
const MIN_REFRESH_INTERVAL_MS = 1000; // Minimum 1 second between refresh attempts
let consecutiveRefreshFailures = 0; // Track consecutive failures for circuit breaker
const MAX_CONSECUTIVE_FAILURES = 3; // Circuit breaker threshold
let circuitBreakerUntil = 0; // Timestamp when circuit breaker expires
const CIRCUIT_BREAKER_DURATION_MS = 5000; // 5 seconds cooldown after multiple failures

// Flag to prevent new requests during logout
let isLoggingOut = false;

/** Routes where a 401 must not force redirect to /login (SPA still handles the screen). */
function isAuthRelaxedPublicPath(): boolean {
    const p = window.location.pathname;
    return (
        p === "/login" ||
        p === "/register" ||
        p === "/password-reset" ||
        p === "/password-reset/confirm" ||
        p === "/reset-password" ||
        p === "/reset-password/confirm" ||
        // Utdelarlänken körs helt utan konto — ett 401 från någon bakgrundsfråga
        // får inte kasta ut besökaren till inloggningen.
        p === "/uppdrag"
    );
}

/**
 * Reset refresh failure flag (call after successful login)
 */
export function resetRefreshFailure(): void {
    refreshFailed = false;
    isRefreshing = false;
    refreshPromise = null;
    refreshAttempts = 0;
    lastRefreshAttemptTime = 0;
    consecutiveRefreshFailures = 0;
    circuitBreakerUntil = 0;
    isLoggingOut = false;
    console.log("[Auth] Refresh state reset after successful login");
}

/**
 * Set logout flag to block new requests during logout
 */
export function setLoggingOut(value: boolean): void {
    isLoggingOut = value;
    if (value) {
        console.log("[Auth] Logout flag set - blocking new requests");
    }
}

/**
 * Generates a correlation ID for request tracking (GUID format, no dashes).
 * Uses crypto.randomUUID if available, otherwise falls back to a simple implementation.
 */
function generateCorrelationId(): string {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        // Remove dashes for shorter, URL-safe format (matches backend format)
        return crypto.randomUUID().replace(/-/g, "");
    }

    // Fallback: simple UUID v4 implementation
    return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// Correlation ID: Generate per request and include in headers
apiClient.interceptors.request.use((config) => {
    // Block all requests except logout if we're logging out
    if (isLoggingOut && !config.url?.includes("/auth/logout")) {
        console.log("[Auth] Blocking request during logout:", config.url);
        return Promise.reject(new Error("Request blocked: logout in progress"));
    }

    // Generate correlation ID for this request (or reuse if already set)
    if (!config.headers["X-Correlation-ID"]) {
        config.headers["X-Correlation-ID"] = generateCorrelationId();
    }

    // Check for mobile app token in localStorage and add as Bearer token
    // This allows mobile WebView to authenticate using the token from the mobile app
    const mobileToken = localStorage.getItem('mobileAccessToken');
    if (mobileToken && !config.headers["Authorization"]) {
        config.headers["Authorization"] = `Bearer ${mobileToken}`;
        console.log('[Auth] Using mobile token for request');
    }

    // Build cache key from URL and params
    const cacheKey = buildCacheKey(config.url || "", config.params);

    // Add If-None-Match header if we have a stored ETag
    const storedETag = getStoredETag(cacheKey);
    if (storedETag && !config.headers["If-None-Match"]) {
        config.headers["If-None-Match"] = storedETag;
        // Store cache key on config for use in response interceptor
        (config as any).__cacheKey = cacheKey;
    }

    return config;
});

// Phase 4: Handle 304 responses and store ETags
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        const cacheKey = (response.config as any).__cacheKey || buildCacheKey(
            response.config.url || "",
            response.config.params
        );

        // Store ETag from response for next request
        const etag = response.headers["etag"];
        if (etag) {
            storeETag(cacheKey, etag);
            // Also store the response data for potential 304 handling
            storeCachedData(cacheKey, response.data);
        }

        return response;
    },
    async (error) => {
        // Handle 304 Not Modified: Return cached data
        if (error.response?.status === 304) {
            const cacheKey = (error.config as any).__cacheKey || buildCacheKey(
                error.config.url || "",
                error.config.params
            );

            const cachedData = getCachedData(cacheKey);
            if (cachedData) {
                // Return a fake response with cached data
                return {
                    ...error.response,
                    data: cachedData,
                    status: 200,
                    statusText: "OK",
                    headers: {
                        ...error.response.headers,
                        "x-cached": "true"
                    }
                } as AxiosResponse;
            }
            // If no cached data, fall through to 401 handling
        }

        // Handle 401 errors by attempting refresh, then redirecting to login
        // CRITICAL FIX: Multiple layers of protection to prevent refresh loops

        // Check if this is a refresh request itself - use multiple checks for safety
        const isRefreshRequest =
            error.config?._isRefreshRequest === true || // Internal flag (most reliable)
            error.config?.url?.includes("/auth/refresh") ||
            error.config?.url?.includes("/api/auth/refresh") ||
            error.config?.url?.endsWith("/refresh"); // Extra safety check

        // Check if this is a logout request - never try to refresh on logout
        const isLogoutRequest =
            error.config?._isLogoutRequest === true || // Internal flag (most reliable)
            error.config?.url?.includes("/auth/logout") ||
            error.config?.url?.includes("/api/auth/logout") ||
            error.config?.url?.endsWith("/logout"); // Extra safety check

        // If refresh request itself gets 401, mark as failed immediately and DON'T try again
        if (error.response?.status === 401 && isRefreshRequest) {
            console.warn("[Auth] Refresh request failed with 401 - marking as failed");
            refreshFailed = true;
            refreshAttempts = MAX_REFRESH_ATTEMPTS + 1; // Mark as exceeded
            isRefreshing = false;
            refreshPromise = null;
            lastRefreshAttemptTime = Date.now(); // Record failure time

            // Redirect to login unless we're on a public auth flow page
            if (!isAuthRelaxedPublicPath()) {
                console.log("[Auth] Redirecting to /login due to refresh failure");
                window.location.href = "/login";
            }

            // Reject immediately - don't process further
            return Promise.reject(error);
        }

        // For non-refresh 401 errors, only try refresh if:
        // 1. Refresh hasn't failed yet
        // 2. We haven't exceeded max attempts
        // 3. This request hasn't been retried yet
        // 4. This is NOT a refresh request (extra safety check)
        // 5. This is NOT a logout request (never refresh on logout)
        // 6. Enough time has passed since last refresh attempt (cooldown)
        // 7. Circuit breaker is not active
        const now = Date.now();
        const timeSinceLastRefresh = now - lastRefreshAttemptTime;
        const isCircuitBreakerActive = now < circuitBreakerUntil;

        if (isCircuitBreakerActive) {
            console.warn(
                `[Auth] Circuit breaker active until ${new Date(circuitBreakerUntil).toISOString()}, refusing refresh`
            );
        }

        const canAttemptRefresh =
            error.response?.status === 401 &&
            !error.config._retry &&
            !isRefreshRequest &&
            !isLogoutRequest &&
            !refreshFailed &&
            refreshAttempts < MAX_REFRESH_ATTEMPTS &&
            timeSinceLastRefresh >= MIN_REFRESH_INTERVAL_MS &&
            !isCircuitBreakerActive;

        if (canAttemptRefresh) {
            console.log("[Auth] 401 detected, attempting token refresh");
            error.config._retry = true;

            try {
                // Use mutex to prevent multiple simultaneous refresh attempts
                // This prevents rate limiting issues when many requests fail with 401 at once
                if (!isRefreshing) {
                    isRefreshing = true;
                    refreshAttempts++;
                    lastRefreshAttemptTime = Date.now();
                    console.log(
                        `[Auth] Starting refresh attempt ${refreshAttempts}/${MAX_REFRESH_ATTEMPTS}`
                    );

                    refreshPromise = (async () => {
                        try {
                            // CRITICAL: Mark this request explicitly so it never triggers refresh logic
                            await apiClient.post(
                                "/auth/refresh",
                                {},
                                {
                                    _isRefreshRequest: true, // Internal flag to prevent recursion
                                } as any
                            );

                            // Refresh succeeded - reset ALL counters including circuit breaker
                            console.log("[Auth] Refresh succeeded");
                            refreshFailed = false;
                            refreshAttempts = 0;
                            consecutiveRefreshFailures = 0; // Reset circuit breaker on success
                            circuitBreakerUntil = 0;
                        } catch (refreshErr: any) {
                            // Refresh failed - mark as failed to prevent further attempts
                            console.error("[Auth] Refresh failed:", refreshErr);
                            refreshFailed = true;
                            refreshAttempts = MAX_REFRESH_ATTEMPTS + 1;

                            // Track consecutive failures and activate circuit breaker if needed
                            consecutiveRefreshFailures++;
                            if (consecutiveRefreshFailures >= MAX_CONSECUTIVE_FAILURES) {
                                circuitBreakerUntil = Date.now() + CIRCUIT_BREAKER_DURATION_MS;
                                console.error(
                                    `[Auth] Circuit breaker activated after ${consecutiveRefreshFailures} failures, blocked until ${new Date(circuitBreakerUntil).toISOString()}`
                                );
                            }

                            // If refresh itself got 401, stop immediately
                            if (refreshErr?.response?.status === 401) {
                                if (!isAuthRelaxedPublicPath()) {
                                    console.log(
                                        "[Auth] Refresh got 401, redirecting to /login"
                                    );
                                    window.location.href = "/login";
                                }
                            }

                            throw refreshErr;
                        } finally {
                            isRefreshing = false;
                            refreshPromise = null;
                        }
                    })();
                } else {
                    console.log("[Auth] Refresh already in progress, waiting...");
                }

                // Wait for refresh to complete (whether it's the one we started or one already in progress)
                if (refreshPromise) {
                    await refreshPromise;
                }

                // Only retry if refresh succeeded and we're not in failed state
                if (!refreshFailed && refreshAttempts <= MAX_REFRESH_ATTEMPTS) {
                    return apiClient.request(error.config);
                }
            } catch (refreshError) {
                // Refresh failed - stop trying and redirect to login immediately
                console.error("[Auth] Refresh error caught in outer catch:", refreshError);
                refreshFailed = true;
                refreshAttempts = MAX_REFRESH_ATTEMPTS + 1;
                isRefreshing = false;
                refreshPromise = null;
                lastRefreshAttemptTime = Date.now();

                if (!isRefreshRequest && !isAuthRelaxedPublicPath()) {
                    console.log("[Auth] Redirecting to /login after refresh error");
                    window.location.href = "/login";
                }

                // Return the original error to prevent further processing
                return Promise.reject(error);
            }
        } else if (error.response?.status === 401 && !canAttemptRefresh) {
            // 401 but we can't attempt refresh - log why
            console.log("[Auth] 401 detected but cannot refresh:", {
                alreadyRetried: error.config._retry,
                isRefreshRequest,
                refreshFailed,
                refreshAttempts,
                maxAttempts: MAX_REFRESH_ATTEMPTS,
                timeSinceLastRefresh,
                minInterval: MIN_REFRESH_INTERVAL_MS,
                isCircuitBreakerActive,
                circuitBreakerUntil: circuitBreakerUntil
                    ? new Date(circuitBreakerUntil).toISOString()
                    : null,
            });
        }

        // If refresh has already failed or exceeded attempts, just redirect to login immediately
        // Don't try refresh again (unless it's a logout request, which should not redirect here)
        if (
            error.response?.status === 401 &&
            (refreshFailed || refreshAttempts >= MAX_REFRESH_ATTEMPTS) &&
            !isRefreshRequest &&
            !isLogoutRequest &&
            !isAuthRelaxedPublicPath()
        ) {
            console.log(
                "[Auth] 401 with failed refresh state, redirecting to /login"
            );
            window.location.href = "/login";
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);